import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, FileDownload as FileDownloadIcon, Replay as ReplayIcon } from '@mui/icons-material';
import type { Segment } from '../../types/api.types';
import { useSegments, useDeleteSegment } from '../../hooks/useSegments';
import explainerService from '../../services/explainer.service';

// ── Helpers ────────────────────────────────────────────────────────────────

/** Format a timestamp in seconds to m:ss, e.g. "1:09". */
function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

/** Format a duration in seconds to m:ss, e.g. "0:23". */
function formatDuration(seconds: number): string {
  return formatTime(seconds);
}

// ── Chip state priority: active > last-worked > proximity > default ────────

type ChipState = 'active' | 'last-worked' | 'proximity' | 'default';

function getChipState(
  segId: number,
  activeSegmentId: number | null,
  lastWorkedSegmentId: number | null,
  proximitySegmentId: number | null,
): ChipState {
  if (activeSegmentId === segId) return 'active';
  if (lastWorkedSegmentId === segId) return 'last-worked';
  if (proximitySegmentId === segId) return 'proximity';
  return 'default';
}

function chipStyleForState(state: ChipState): {
  color: 'primary' | 'default';
  variant: 'filled' | 'outlined';
  extraSx?: object;
} {
  switch (state) {
    case 'active':
      return { color: 'primary', variant: 'filled' };
    case 'last-worked':
      return {
        color: 'primary',
        variant: 'outlined',
        extraSx: { borderLeft: '4px solid', borderColor: 'primary.main' },
      };
    case 'proximity':
      return {
        color: 'default',
        variant: 'outlined',
        extraSx: { outline: '2px solid', outlineColor: 'primary.main' },
      };
    default:
      return { color: 'default', variant: 'outlined' };
  }
}

// ── Props ──────────────────────────────────────────────────────────────────

interface PodcastSegmentChipStripProps {
  projectId: number;
  episodeId: number;
  activeSegmentId: number | null;
  currentTime: number;
  lastWorkedSegmentId: number | null;
  onSegmentClick: (segment: Segment) => void;
  onLastWorkedChange: (segmentId: number | null) => void;
  /** Fired during free playback whenever the playhead enters/leaves a segment. */
  onProximitySegmentChange?: (segment: Segment | null) => void;
  /** If provided, shows a Download icon button in the header for practice-reading transcript export. */
  onDownloadTranscripts?: () => void;
  /** Called when the user clicks the edit (pencil) icon on a chip. */
  onEditSegment?: (segment: Segment) => void;
  /** Called after the user confirms deletion, so the parent can stop playback if the segment was active. */
  onSegmentDeleted?: (segment: Segment) => void;
}

/**
 * PodcastSegmentChipStrip — horizontally scrollable segment chip strip for the
 * podcast player, mirroring the YouTube VideoPlayerSegmentList UX pattern.
 *
 * Features:
 * - Four chip visual states: active > last-worked > proximity > default
 * - Click-to-seek: calls onSegmentClick which triggers AudioPlayer seek
 * - Delete chip with MUI Dialog confirmation
 * - Resume button in header when lastWorkedSegmentId is set
 * - Auto-scroll to active / proximity chip
 * - ARIA tablist/tab semantics + roving tabindex keyboard nav
 */
export const PodcastSegmentChipStrip = ({
  projectId,
  episodeId,
  activeSegmentId,
  currentTime,
  lastWorkedSegmentId,
  onSegmentClick,
  onLastWorkedChange,
  onProximitySegmentChange,
  onDownloadTranscripts,
  onEditSegment,
  onSegmentDeleted,
}: PodcastSegmentChipStripProps) => {
  const { t } = useTranslation('segments');
  const { data: segments = [], isLoading } = useSegments(projectId, episodeId);
  const { mutate: deleteSegment } = useDeleteSegment();
  const stripRef = useRef<HTMLDivElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<Segment | null>(null);
  const [explanationBySegment, setExplanationBySegment] = useState<Record<number, boolean>>({});

  // ── Proximity: segment whose range contains the current playhead ──────────
  const proximitySegmentId = useMemo<number | null>(() => {
    if (activeSegmentId !== null) return null; // suppressed when a segment is explicitly selected
    const found = segments.find(
      (s) => s.startTime <= currentTime && currentTime < s.endTime,
    );
    return found?.id ?? null;
  }, [segments, currentTime, activeSegmentId]);

  // ── Auto-scroll: active chip ──────────────────────────────────────────────
  useEffect(() => {
    if (!stripRef.current || activeSegmentId === null) return;
    const chip = stripRef.current.querySelector<HTMLElement>(
      `[data-segment-id="${activeSegmentId}"]`,
    );
    chip?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [activeSegmentId]);

  // ── Auto-scroll: proximity chip (suppressed when segment is active) ───────
  useEffect(() => {
    if (!stripRef.current || activeSegmentId !== null || proximitySegmentId === null) return;
    const chip = stripRef.current.querySelector<HTMLElement>(
      `[data-segment-id="${proximitySegmentId}"]`,
    );
    chip?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [proximitySegmentId, activeSegmentId]);

  // ── Notify parent when playhead enters/leaves a segment during free play ──
  useEffect(() => {
    // Only fire during free playback (no chip explicitly selected).
    // When activeSegmentId is set, proximitySegmentId is already null but we
    // must not propagate that null or it would clear the explicitly-selected panel.
    if (!onProximitySegmentChange || activeSegmentId !== null) return;
    const seg = proximitySegmentId !== null
      ? segments.find((s) => s.id === proximitySegmentId) ?? null
      : null;
    onProximitySegmentChange(seg);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proximitySegmentId]);

  const segmentIds = segments.map((s) => s.id).join(',');

  useEffect(() => {
    let cancelled = false;

    if (segments.length === 0) {
      setExplanationBySegment({});
      return;
    }

    const loadExplanationStatus = async () => {
      try {
        const ids = segments.map((s) => s.id);
        const status = await explainerService.getExplanationStatus(ids);
        if (cancelled) return;
        const numericStatus: Record<number, boolean> = {};
        for (const [k, v] of Object.entries(status)) {
          numericStatus[Number(k)] = v;
        }
        setExplanationBySegment(numericStatus);
      } catch {
        if (cancelled) return;
        setExplanationBySegment({});
      }
    };

    loadExplanationStatus();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentIds]);

  // ── Delete handler ────────────────────────────────────────────────────────
  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    const wasActive = deleteTarget.id === activeSegmentId;
    const wasLastWorked = deleteTarget.id === lastWorkedSegmentId;
    deleteSegment({ id: deleteTarget.id, projectId, episodeId });
    if (wasActive || wasLastWorked) {
      onLastWorkedChange(null);
    }
    onSegmentDeleted?.(deleteTarget);
    setDeleteTarget(null);
  };

  // ── Resume handler ────────────────────────────────────────────────────────
  const handleResume = () => {
    if (lastWorkedSegmentId === null) return;
    const seg = segments.find((s) => s.id === lastWorkedSegmentId);
    if (seg) onSegmentClick(seg);
  };

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Box sx={{ px: 2, py: 2.5 }}>
        <Typography variant="overline" color="text.secondary" gutterBottom display="block">
          {t('segments')}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" width={90} height={32} />
          ))}
        </Stack>
      </Box>
    );
  }

  // ── Empty state ───────────────────────────────────────────────────────────
  if (segments.length === 0) {
    return (
      <Box sx={{ px: 2, py: 2.5 }}>
        <Typography variant="overline" color="text.secondary" display="block">
          {t('segments')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('noSegmentsYet')}
        </Typography>
      </Box>
    );
  }

  // ── Chip strip ────────────────────────────────────────────────────────────
  return (
    <Box sx={{ px: 2, py: 2.5 }}>
      {/* Header row: label + optional Resume + optional Download buttons */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="overline" color="text.secondary" display="block">
          {t('segments')}
        </Typography>
        <Stack direction="row" alignItems="center">
          {lastWorkedSegmentId !== null && (
            <Tooltip title={t('resumeLastSegment')}>
              <IconButton
                size="small"
                aria-label={t('resumeLastSegment')}
                onClick={handleResume}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDownloadTranscripts != null && (
            <Tooltip title={t('downloadTranscripts', 'Download transcripts for practice reading')}>
              <IconButton
                size="small"
                aria-label={t('downloadTranscripts', 'Download transcripts for practice reading')}
                onClick={onDownloadTranscripts}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {/* Horizontal scrollable strip — no wrapping */}
      <Stack
        ref={stripRef}
        direction="row"
        spacing={1}
        role="tablist"
        aria-label={t('segments')}
        sx={{ overflowX: 'auto', flexWrap: 'nowrap', pt: 0.5, pb: 0.5 }}
      >
        {segments.map((seg, index) => {
          const isActive = activeSegmentId === seg.id;
          const hasExplanation = explanationBySegment[seg.id];
          const chipState = getChipState(seg.id, activeSegmentId, lastWorkedSegmentId, proximitySegmentId);
          const { color, variant, extraSx } = chipStyleForState(chipState);

          const label = `${formatTime(seg.startTime)} – ${formatTime(seg.endTime)}`;
          const duration = seg.endTime - seg.startTime;

          // Roving tabindex: active chip (or first chip when none active) is Tab-reachable;
          // Arrow keys move focus within the strip.
          const tabIndex = activeSegmentId !== null ? (isActive ? 0 : -1) : index === 0 ? 0 : -1;

          const primaryTextColor = isActive ? 'common.white' : 'text.primary';
          const secondaryTextColor = isActive ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary';
          const chipLabel = (
            <Stack direction="column" alignItems="center" spacing={0} sx={{ py: 0.25 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.2, color: primaryTextColor }}>
                {label}
              </Typography>
              <Typography variant="caption" sx={{ lineHeight: 1.2, color: secondaryTextColor }}>
                {formatDuration(duration)}
              </Typography>
              {hasExplanation && (
                <Typography variant="caption" sx={{ lineHeight: 1.2, color: secondaryTextColor }}>
                  {t('explainedChip', 'Explained')}
                </Typography>
              )}
            </Stack>
          );

          const chipLabelWithIcons = (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                component="span"
                role="button"
                aria-label={t('editSegment', 'Edit Segment')}
                onClick={(e) => { e.stopPropagation(); if (onEditSegment) onEditSegment(seg); }}
                sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', p: 0.25 }}
              >
                <EditIcon fontSize="small" />
              </Box>
              {chipLabel}
              <Box
                component="span"
                role="button"
                aria-label={t('deleteSegment')}
                onClick={(e) => { e.stopPropagation(); setDeleteTarget(seg); }}
                sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', p: 0.25 }}
              >
                <DeleteIcon fontSize="small" />
              </Box>
            </Stack>
          );

          return (
            <Box key={seg.id} data-chip-state={chipState} sx={{ flexShrink: 0 }}>
              <Chip
                data-segment-id={seg.id}
                label={chipLabelWithIcons}
                onClick={() => onSegmentClick(seg)}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowRight') {
                    event.preventDefault();
                    const tabs = stripRef.current?.querySelectorAll<HTMLElement>('[role="tab"]');
                    if (tabs) (tabs[(index + 1) % tabs.length])?.focus();
                  } else if (event.key === 'ArrowLeft') {
                    event.preventDefault();
                    const tabs = stripRef.current?.querySelectorAll<HTMLElement>('[role="tab"]');
                    if (tabs) (tabs[(index - 1 + tabs.length) % tabs.length])?.focus();
                  }
                }}
                color={color}
                variant={variant}
                size="medium"
                role="tab"
                aria-selected={isActive}
                aria-label={label}
                tabIndex={tabIndex}
                sx={{ minHeight: 56, cursor: 'pointer', ...extraSx }}
              />
            </Box>
          );
        })}
      </Stack>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('deleteSegmentTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('deleteSegmentConfirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('cancel')}</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            {t('deleteSegment')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
